import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MyAppText from '../../../components/MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { translate } from '../../../services/translate';
import CustomButton from '../../../components/CustomButton';
import { Tabs } from '../../../components/Tabs';
import FormInput from '../../../components/FormInput';
import { WindowInformation } from '../../../services/window-information';
import { faCheck, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../../context/Auth';
import Toast from 'react-native-toast-message';
import { AlreadyExists, ClientError, InvalidValue } from '../../../services/central-api/base-service';
import { RouteProp, useFocusEffect, useIsFocused, useRoute } from '@react-navigation/native';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { useForm } from 'react-hook-form';
import { ApprovedCompaniesFiles, CloudProviderConnectionFormParams, Inscription, InscriptionFormObject, VideoAnalysisFormParams } from '../../../typings/third-party-approved-companies';
import { CompanyType, InscriptionStatus, DocumentType, thirdPartyApprovedCompaniesService, companyType, CompanyTypeWithoutBoth } from '../../../services/central-api/third-party-approved-companies';
import MyPhoneInput from '../../../components/MyPhoneInput';
import MyCnpjInput from '../../../components/MyCnpjInput';
import MyEmailInput from '../../../components/MyEmailInput';
import FormDropDownPicker from '../../../components/FormDropDownPicker';
import FormFileUploader from '../../../components/FormFileUploader';
import { MyCheckbox } from '../../../components/MyCheckbox';
import { parseTextWithBold } from '../../deac/DeacCommons';
import { useQuery } from '@tanstack/react-query';
import InscriptionCard from './InscriptionCard';
import DeleteInscriptionModal from './DeleteInscriptionModal';
import { createFileFromUrl, generateInscriptionCsv, getInscriptionStatus, SectionHeader, validUfs } from '../InscriptionsCommons';
import InscriptionChangeStatusModal from './InscriptionChangeStatusModal';
import InscriptionPublishConfirmModal from './InscriptionPublishConfirmModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import VideoAnalysisForm from './VideoAnalysisForm';
import CloudProviderDropdown from '../../../components/third-party-approved-companies/CloudProviderDropdown';
import CloudProviderConnectionForm from './CloudProviderConnectionForm';
import WarningModal from '../../../components/WarningModal';
import { validateCPF } from '../../../components/helpers/documents';
import ImageProviderCompanies from './ImageProviderCompanies';
import { ThirdPartyApprovedCompaniesParamList } from '../../../typings/Params';
import { RootNavigation } from '../../../Routes';

const defaultEmptyValues: InscriptionFormObject = {
    status: 'pending',
    address: '',
    businessEmail: '',
    cep: '',
    city: '',
    cnpj: '',
    commercialPhone: '',
    companyName: '',
    companyType: [],
    companyCloudProviderId: undefined,
    document: '',
    documentType: DocumentType.CPF,
    integratedCloudCamerasQty: 0,
    integratedImageCamerasQty: 0,
    name: '',
    neighborhood: '',
    number: 0,
    phone: '',
    role: '',
    state: '',
    tradeName: '',
    companyWebsite: '',
    publicCallAcceptance: false,
};

const defaultFilesEmptyValues: ApprovedCompaniesFiles = {
    visualIdentity: {
        accepted: null,
        fileName: '',
        file: undefined,
    },
    assignmentAgreement: {
        accepted: null,
        fileName: '',
        file: undefined,
    },
    companyAdministratorDocument: {
        accepted: null,
        fileName: '',
        file: undefined,
    },
    legalEntityRegistrationProofAndStatus: {
        accepted: null,
        fileName: '',
        file: undefined,
    },
    socialContract: {
        accepted: null,
        fileName: '',
        file: undefined,
    }
};

const defaultVideoAnalysisValues: VideoAnalysisFormParams = {
    responseTimeLive: null,
    responseTimeTimeline: null,
    imageQualityLive: null,
    imageQualityTimeline: null,
    framerateLive: null,
    framerateTimeline: null,
    timelineConsistency: null,
    resolution: '',
    cameraGeolocation: null,
};

const defaultCloudProviderConnectionValues: CloudProviderConnectionFormParams = {
    cloudProviderUsername: null,
    cloudProviderEndpoint: null,
    cloudProviderPassword: null
};

export default function InscriptionForm({ navigation }: {
    navigation: RootNavigation;
}) {
    const route = useRoute<RouteProp<ThirdPartyApprovedCompaniesParamList, 'InscriptionForm'>>();
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const form = useForm<InscriptionFormObject>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const filesForm = useForm<ApprovedCompaniesFiles>({
        defaultValues: defaultFilesEmptyValues,
        mode: 'onChange'
    });

    const additionalDocuments = useForm<{
        files: {
            file: File | undefined;
            fileName: string;
            fileId: string;
        }[];
    }>({
        defaultValues: { files: [] },
        mode: 'onChange'
    });

    const videoAnalysisForm = useForm<VideoAnalysisFormParams>({
        defaultValues: defaultVideoAnalysisValues,
        mode: 'onChange'
    });

    const cloudProviderConnectionForm = useForm<CloudProviderConnectionFormParams>({
        defaultValues: defaultCloudProviderConnectionValues,
        mode: 'onChange'
    });

    const [inscription, setInscription] = useState<Inscription | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingActions, setIsLoadingActions] = useState<boolean>(false);
    const [isChangeStatusModalVisible, setIsChangeStatusModalVisible] = useState<boolean>(false);
    const [isPublishUnpublishModalVisible, setIsPublishUnpublishModalVisible] = useState<boolean>(false);
    const [publishMode, setPublishMode] = useState<'publish' | 'unpublish'>('publish');
    const [observationText, setObservationText] = useState<string>('');
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [isPopulatingForms, setIsPopulatingForms] = useState<boolean>(false);
    const [finishedInfoFetch, setFinishedInfoFetch] = useState<boolean>(false);
    const [showWarningModal, setShowWarningModal] = useState<boolean>(false);

    const inscriptionId = route.params?.id || '';
    const queryResult = useQuery({
        queryKey: ['inscriptionForm', inscriptionId],
        queryFn: () => thirdPartyApprovedCompaniesService.getInscription(inscriptionId!),
        enabled: !!inscriptionId && inscriptionId.length > 0 && !!authenticatedUser,
    });

    const isEnabled = !!inscriptionId && !!authenticatedUser;
    const isFetchingInformation = isEnabled && (queryResult.isFetching || isPopulatingForms);

    const { data, refetch, error, isError } = queryResult;

    function getCompanyType(companyType: CompanyType): CompanyTypeWithoutBoth[] {
        switch (companyType) {
            case 'cloud': return ['cloud'];
            case 'images': return ['images'];
            case 'both': return ['cloud', 'images'];
        }
    }

    async function setForms(data: Inscription) {
        try {
            setFinishedInfoFetch(true);
            setIsPopulatingForms(true);
            setInscription(data);
            const companyType = getCompanyType(data.companyType);
            form.reset({
                status: data.status,
                documentType: data.documentType,
                document: data.document,
                name: data.name,
                phone: data.phone,
                role: data.role,
                companyName: data.companyName,
                tradeName: data.tradeName,
                companyType: companyType,
                integratedCloudCamerasQty: data.thirdPartyCamerasForCloudCount,
                integratedImageCamerasQty: data.thirdPartyCamerasForImageCount,
                companyCloudProviderId: data.cloudProviderId,
                cnpj: data.cnpj,
                businessEmail: data.businessEmail,
                address: data.address,
                cep: data.cep,
                city: data.city,
                commercialPhone: data.commercialPhone,
                companyWebsite: data.companyWebsite?.split('://')[1] || '',
                neighborhood: data.neighborhood,
                number: data.number,
                state: data.state.toUpperCase(),
                publicCallAcceptance: data.publicCallAcceptance
            });

            videoAnalysisForm.reset({
                responseTimeLive: data.responseTimeLive,
                responseTimeTimeline: data.responseTimeTimeline,
                imageQualityLive: data.imageQualityLive,
                imageQualityTimeline: data.imageQualityTimeline,
                framerateLive: data.framerateLive,
                framerateTimeline: data.framerateTimeline,
                timelineConsistency: data.timelineConsistency,
                resolution: data.resolution,
                cameraGeolocation: data.cameraGeolocation,
            });

            cloudProviderConnectionForm.reset({
                cloudProviderUsername: data.cloudProviderUsername,
                cloudProviderEndpoint: data.cloudProviderEndpoint,
                cloudProviderPassword: data.cloudProviderPassword,
            });

            filesForm.reset({
                visualIdentity: undefined,
                legalEntityRegistrationProofAndStatus: undefined,
                companyAdministratorDocument: undefined,
                socialContract: undefined,
                assignmentAgreement: undefined
            });

            if (data.files && data.files.visualIdentity && data.files.visualIdentity.fileUrl) {
                const visualIdentityFile = await createFileFromUrl(
                    data.files.visualIdentity.fileUrl,
                    data.files.visualIdentity.fileName
                );

                filesForm.setValue('visualIdentity', {
                    file: visualIdentityFile,
                    fileName: visualIdentityFile?.name || '',
                    accepted: data.files.visualIdentity.accepted || null
                }, {
                    shouldValidate: false,
                    shouldDirty: false
                });
            }

            if (data.files && data.files.legalEntityRegistrationProofAndStatus && data.files.legalEntityRegistrationProofAndStatus.fileUrl) {
                const legalEntityRegistrationProofAndStatusFile = await createFileFromUrl(
                    data.files.legalEntityRegistrationProofAndStatus.fileUrl,
                    data.files.legalEntityRegistrationProofAndStatus.fileName
                );

                filesForm.setValue('legalEntityRegistrationProofAndStatus', {
                    file: legalEntityRegistrationProofAndStatusFile,
                    fileName: legalEntityRegistrationProofAndStatusFile?.name || '',
                    accepted: data.files.legalEntityRegistrationProofAndStatus.accepted || null
                }, {
                    shouldValidate: false,
                    shouldDirty: false
                });
            }

            if (data.files && data.files.companyAdministratorDocument && data.files.companyAdministratorDocument.fileUrl) {
                const companyAdministratorDocumentFile = await createFileFromUrl(
                    data.files.companyAdministratorDocument.fileUrl,
                    data.files.companyAdministratorDocument.fileName
                );

                filesForm.setValue('companyAdministratorDocument', {
                    file: companyAdministratorDocumentFile,
                    fileName: companyAdministratorDocumentFile?.name || '',
                    accepted: data.files.companyAdministratorDocument.accepted || null
                }, {
                    shouldValidate: false,
                    shouldDirty: false
                });
            }

            if (data.files && data.files.socialContract && data.files.socialContract.fileUrl) {
                const socialContractFile = await createFileFromUrl(
                    data.files.socialContract.fileUrl,
                    data.files.socialContract.fileName
                );
                filesForm.setValue('socialContract', {
                    file: socialContractFile,
                    fileName: socialContractFile?.name || '',
                    accepted: data.files.socialContract.accepted || null
                }, {
                    shouldValidate: false,
                    shouldDirty: false
                });
            }

            if (data.files && data.files.assignmentAgreement && data.files.assignmentAgreement.fileUrl) {
                const assignmentAgreementFile = await createFileFromUrl(
                    data.files.assignmentAgreement.fileUrl,
                    data.files.assignmentAgreement.fileName
                );
                filesForm.setValue('assignmentAgreement', {
                    file: assignmentAgreementFile,
                    fileName: assignmentAgreementFile?.name || '',
                    accepted: data.files.assignmentAgreement.accepted || null
                }, {
                    shouldValidate: false,
                    shouldDirty: false
                });
            }

            additionalDocuments.reset({
                files: []
            });

            if (data.extraFiles) {
                const extraFiles = [];
                for (const fileInfo of data.extraFiles.files) {
                    const file = await createFileFromUrl(
                        fileInfo.fileUrl,
                        fileInfo.fileName
                    );
                    extraFiles.push({
                        file: file,
                        fileName: fileInfo.fileName,
                        fileId: fileInfo.fileId,
                    });
                }

                additionalDocuments.setValue('files', extraFiles, {
                    shouldValidate: false,
                    shouldDirty: false
                });
            }
        } finally {
            setIsPopulatingForms(false);
        }
    }

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        setForms(data);

    }, [inscriptionId, data, authenticatedUser]);

    useEffect(() => {
        if (isError) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' });
        }
    }, [error, isError]);

    useEffect(() => {
        if (!isPopulatingForms && finishedInfoFetch) {
            setTimeout(async () => {
                await Promise.all([
                    form.trigger(),
                    filesForm.trigger()
                ]);
                setFinishedInfoFetch(false);
            }, 150);
        }
    }, [isPopulatingForms, finishedInfoFetch]);

    useEffect(() => {
        if (!isPopulatingForms && !finishedInfoFetch) {
            form.setValue('document', '');
            form.trigger('document');
        }
    }, [form.watch('documentType')]);

    useEffect(() => {
        if (!isPopulatingForms && !finishedInfoFetch) {
            if (form.watch('companyType').includes('cloud')) {
                form.setValue('companyCloudProviderId', undefined);
            }
        }
    }, [form.watch('companyType')]);

    const companyTypeItems = useMemo(() => {
        return Object.values(companyType)
            .filter(v => v != 'both')
            .map((val) => ({
                label: translate(`${val}InscriptionType`),
                value: val,
            }));
    }, []);

    const documentTypeItems = useMemo(() => {
        return Object.values(DocumentType)
            .map((val) => ({
                label: val.toUpperCase(),
                value: val,
            }));
    }, []);

    const IsEditing = route.params?.id ? true : false;
    const inViewMode = (IsEditing && inscription && ['published', 'failed'].includes(inscription.status)) || false;
    const needVideoAnalysis = (IsEditing && inscription && ['both', 'images'].includes(inscription.companyType) && ['published', 'approved', 'failed'].includes(inscription.status));
    const isEditingCloudProvider = (IsEditing && inscription && ['both', 'cloud'].includes(inscription.companyType) && ['published', 'approved', 'failed'].includes(inscription.status));

    function cleanValues() {
        form.reset(defaultEmptyValues);
        filesForm.reset(defaultFilesEmptyValues);
        videoAnalysisForm.reset(defaultVideoAnalysisValues);
        cloudProviderConnectionForm.reset(defaultCloudProviderConnectionValues);
        setInscription(null);
        setIsLoading(false);
        setIsLoadingActions(false);
        setIsPopulatingForms(false);
        setFinishedInfoFetch(false);
        setObservationText('');
    }

    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            cleanValues();

            if (route.params?.id) {
                refetch();
            }
        }
    }, [isFocused, navigation, route.params]));

    function checkConnectionInfoChanged(shouldGoBack: boolean = false) {
        if (IsEditing) {
            const cloudProviderConnectionInfo = cloudProviderConnectionForm.getValues();

            const username = inscription?.cloudProviderUsername != undefined &&
                cloudProviderConnectionInfo.cloudProviderUsername != null &&
                inscription.cloudProviderUsername != cloudProviderConnectionInfo.cloudProviderUsername;

            const password = inscription?.cloudProviderPassword != undefined &&
                cloudProviderConnectionInfo.cloudProviderPassword != null &&
                inscription.cloudProviderPassword != cloudProviderConnectionInfo.cloudProviderPassword;

            const endpoint = inscription?.cloudProviderEndpoint != undefined &&
                cloudProviderConnectionInfo.cloudProviderEndpoint != null &&
                inscription.cloudProviderEndpoint != cloudProviderConnectionInfo.cloudProviderEndpoint;

            if (username || password || endpoint) {
                setShowWarningModal(true);
                return;
            }
        }
        formSubmit(shouldGoBack);
    }

    async function formSubmit(shouldGoBack: boolean = false) {
        try {
            setIsLoading(true);
            const formData = new FormData();

            const generalData = form.getValues();
            const attachments = filesForm.getValues();

            if (generalData.companyWebsite && generalData.companyWebsite != '' && !generalData.companyWebsite.includes('https://') && !generalData.companyWebsite.includes('http://')) {
                generalData.companyWebsite = 'https://' + generalData.companyWebsite;
            } else if (!generalData.companyWebsite) {
                generalData.companyWebsite = '';
            }
            form.setValue('companyWebsite', generalData.companyWebsite);

            formData.append('managerData', JSON.stringify({
                name: generalData.name,
                roleInCompany: generalData.role,
                documentType: generalData.documentType,
                document: generalData.document,
                phone: generalData.phone,
            }));

            formData.append('companyData', JSON.stringify({
                companyName: generalData.companyName,
                tradeName: generalData.tradeName,
                cnpj: generalData.cnpj,
                commercialPhone: generalData.commercialPhone,
                businessEmail: generalData.businessEmail,
                companyType: generalData.companyType,
                companyCloudProviderId: generalData.companyCloudProviderId ? generalData.companyCloudProviderId : undefined,
                companyWebsite: generalData.companyWebsite,
                cep: generalData.cep,
                address: generalData.address,
                number: generalData.number,
                city: generalData.city,
                neighborhood: generalData.neighborhood,
                state: generalData.state,
            }));

            formData.append('publicCallAcceptance', String(generalData.publicCallAcceptance));
            formData.append('status', String(generalData.status));

            if (attachments.visualIdentity.file) {
                formData.append('visualIdentity', attachments.visualIdentity.file);
                formData.append('visualIdentityMeta', JSON.stringify({
                    accepted: attachments.visualIdentity.accepted
                }));
            }
            if (attachments.legalEntityRegistrationProofAndStatus.file) {
                formData.append('legalEntityRegistrationProofAndStatus', attachments.legalEntityRegistrationProofAndStatus.file);
                formData.append('legalEntityRegistrationProofAndStatusMeta', JSON.stringify({
                    accepted: attachments.legalEntityRegistrationProofAndStatus.accepted
                }));
            }
            if (attachments.companyAdministratorDocument.file) {
                formData.append('companyAdministratorDocument', attachments.companyAdministratorDocument.file);
                formData.append('companyAdministratorDocumentMeta', JSON.stringify({
                    accepted: attachments.companyAdministratorDocument.accepted
                }));
            }
            if (attachments.socialContract.file) {
                formData.append('socialContract', attachments.socialContract.file);
                formData.append('socialContractMeta', JSON.stringify({
                    accepted: attachments.socialContract.accepted
                }));
            }
            if (attachments.assignmentAgreement.file) {
                formData.append('assignmentAgreement', attachments.assignmentAgreement.file);
                formData.append('assignmentAgreementMeta', JSON.stringify({
                    accepted: attachments.assignmentAgreement.accepted
                }));
            }

            if (route.params?.id) {
                formData.append('observationText', observationText);
                const videoAnalysis = videoAnalysisForm.getValues();

                const videoAnalysisData = {
                    ...(videoAnalysis.responseTimeLive !== null && { responseTimeLive: videoAnalysis.responseTimeLive }),
                    ...(videoAnalysis.responseTimeTimeline !== null && { responseTimeTimeline: videoAnalysis.responseTimeTimeline }),
                    ...(videoAnalysis.imageQualityLive !== null && { imageQualityLive: videoAnalysis.imageQualityLive }),
                    ...(videoAnalysis.imageQualityTimeline !== null && { imageQualityTimeline: videoAnalysis.imageQualityTimeline }),
                    ...(videoAnalysis.framerateLive !== null && { framerateLive: videoAnalysis.framerateLive }),
                    ...(videoAnalysis.framerateTimeline !== null && { framerateTimeline: videoAnalysis.framerateTimeline }),
                    ...(videoAnalysis.timelineConsistency !== null && { timelineConsistency: videoAnalysis.timelineConsistency }),
                    ...(videoAnalysis.resolution !== null && { resolution: videoAnalysis.resolution }),
                    ...(videoAnalysis.cameraGeolocation !== null && { cameraGeolocation: videoAnalysis.cameraGeolocation }),
                };
                formData.append('videoAnalysis', JSON.stringify(videoAnalysisData));

                const cloudProviderConnectionInfo = cloudProviderConnectionForm.getValues();
                const cloudProviderConnectionData = {
                    ...(cloudProviderConnectionInfo.cloudProviderUsername !== null && { cloudProviderUsername: cloudProviderConnectionInfo.cloudProviderUsername }),
                    ...(cloudProviderConnectionInfo.cloudProviderPassword !== null && { cloudProviderPassword: cloudProviderConnectionInfo.cloudProviderPassword }),
                    ...(cloudProviderConnectionInfo.cloudProviderEndpoint !== null && { cloudProviderEndpoint: cloudProviderConnectionInfo.cloudProviderEndpoint }),
                };
                formData.append('cloudProviderConnectionInfo', JSON.stringify(cloudProviderConnectionData));

                const updatedInscription = await thirdPartyApprovedCompaniesService.updateInscription(inscriptionId, formData);
                setInscription(updatedInscription);
                cloudProviderConnectionForm.reset({
                    cloudProviderUsername: updatedInscription.cloudProviderUsername || null,
                    cloudProviderPassword: updatedInscription.cloudProviderPassword || null,
                    cloudProviderEndpoint: updatedInscription.cloudProviderEndpoint || null
                });

                const extraFiles = additionalDocuments.getValues();
                let filesToRemove: {
                    fileId: string;
                    fileUrl: string;
                    fileName: string;
                }[] = [];
                let filesToAdd: {
                    file: File | undefined;
                    fileName: string;
                    fileId: string;
                }[] = [];

                if (inscription && inscription.extraFiles) {
                    filesToRemove = inscription.extraFiles.files.filter(
                        f => !extraFiles.files.find(ef => ef.fileId === f.fileId)
                    );
                    filesToAdd = extraFiles.files.filter(
                        ex => !inscription.extraFiles?.files.find(f => f.fileId === ex.fileId)
                    );

                } else {
                    filesToAdd = extraFiles.files;
                }

                let latestExtraFiles = inscription?.extraFiles;
                for (const fileInfo of filesToRemove) {
                    try {
                        latestExtraFiles = await thirdPartyApprovedCompaniesService.deleteExtraFileForInscription(route.params?.id, fileInfo.fileId);
                    } catch (error) {
                        return Toast.show({
                            type: 'sentinelxWarning',
                            text1: translate('fileNotRemoved', { fileName: fileInfo.fileName }),
                        });
                    }
                }

                for (const file of filesToAdd) {
                    try {
                        latestExtraFiles = await thirdPartyApprovedCompaniesService.addExtraFileForInscription(route.params?.id, file.file as File);
                    } catch {
                        return Toast.show({
                            type: 'sentinelxWarning',
                            text1: translate('fileNotAdded', { fileName: file.fileName || file.file?.name }),
                        });
                    }
                }

                if (updatedInscription && latestExtraFiles && (filesToRemove.length > 0 || filesToAdd.length > 0)) {
                    setInscription({
                        ...updatedInscription,
                        extraFiles: latestExtraFiles
                    });

                    const formFiles = await Promise.all(
                        latestExtraFiles.files.map(async (f) => ({
                            file: await createFileFromUrl(f.fileUrl, f.fileName),
                            fileName: f.fileName,
                            fileId: f.fileId
                        }))
                    );

                    additionalDocuments.setValue('files', formFiles, {
                        shouldValidate: false,
                        shouldDirty: false
                    });
                }
            } else {
                const newInscription = await thirdPartyApprovedCompaniesService.createInscription(formData);

                const extraFiles = additionalDocuments.getValues();
                if (extraFiles.files.length > 0) {
                    for (const file of extraFiles.files) {
                        try {
                            await thirdPartyApprovedCompaniesService.addExtraFileForInscription(newInscription.id, file.file as File);
                        } catch {
                            return Toast.show({
                                type: 'sentinelxWarning',
                                text1: translate('fileNotAdded', { fileName: file.fileName || file.file?.name }),
                            });
                        }
                    }
                }

                setInscription(newInscription);
                navigation.setParams({
                    id: newInscription.id
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            if (IsEditing && shouldGoBack) {
                cleanValues();
                navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' });
            }
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('inscriptionValidForCompanyAlreadyExist'),
                });
            } else if (err instanceof InvalidValue) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('invalid_field'),
                });
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    async function updateStatus(values: { status: InscriptionStatus; observationText?: string; }) {
        try {
            setIsLoading(true);
            if (route.params?.id) {
                const formData = new FormData();

                formData.append('status', String(values.status));
                values.observationText && formData.append('observationText', values.observationText);

                const updatedInscription = await thirdPartyApprovedCompaniesService.updateInscription(inscriptionId, formData);
                const infoChanged = form.getValues('status') != updatedInscription.status;

                form.setValue('status', updatedInscription.status);
                if (inscription) {
                    setInscription({
                        ...inscription,
                        status: updatedInscription.status
                    });
                }

                if (infoChanged) {
                    Toast.show({
                        type: 'sentinelxSuccess',
                        text1: translate('ActionSuccessfully'),
                    });
                } else {
                    const allFilesApproved = Object.values(filesForm.watch()).every(f => f && f.accepted);

                    if (!allFilesApproved) {
                        Toast.show({
                            type: 'sentinelxWarning',
                            text1: translate('allFilesMustBeApproved'),
                        });
                    } else {
                        Toast.show({
                            type: 'sentinelxWarning',
                            text1: translate('publicCallMustBeAccepted'),
                        });
                    }
                }
            }
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('inscriptionValidForCompanyAlreadyExist'),
                });
            } else if (err instanceof InvalidValue) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('invalid_field'),
                });
            } else if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    function isFormValid(): boolean {
        return form.formState.isValid && filesForm.formState.isValid;
    }

    function getActions() {
        const actions: Action[] = [];

        const inscriptionStatus = inscription?.status;
        const status = form.watch('status');

        actions.push({
            content: translate('exportCSV'),
            onPress: async () => {
                if (inscription) {
                    await generateInscriptionCsv([inscription.id]);
                }
            }
        });

        actions.push({
            content: translate('changeStatus'),
            onPress: () => setIsChangeStatusModalVisible(true),
            disabled: !isFormValid()
        });

        if (status === 'approved') {
            const allFilesApproved = Object.values(filesForm.watch()).every(f => f && f.accepted);

            if (allFilesApproved) {
                actions.push({
                    content: translate('confirmPublication'),
                    onPress: () => {
                        setPublishMode('publish');
                        setIsPublishUnpublishModalVisible(true);
                    }
                });
            }
        }

        if (inscriptionStatus === 'published') {
            actions.push({
                content: translate('cancelPublication'),
                onPress: () => {
                    setPublishMode('unpublish');
                    setIsPublishUnpublishModalVisible(true);
                }
            });
        }

        actions.push({
            content: translate('deleteInscription'),
            variant: 'danger',
            onPress: () => setIsDeleteModalVisible(true)
        });

        return actions;
    }

    function getActionsOffsetX() {
        if (windowInfo.isMobile) {
            return 0;
        } else {
            if (showSaveButton()) {
                return -4;
            } else {
                return 128;
            }
        }
    }

    function showSaveButton() {
        return !inViewMode || needVideoAnalysis;
    }

    function getFileActions(fieldName: keyof ApprovedCompaniesFiles) {
        const currentValue = filesForm.watch(fieldName);
        return <View style={[{ flexDirection: 'column', gap: 8 }, windowInfo.isMobile && { width: '100%' }]}>
            <MyAppText style={{ fontSize: 14, fontWeight: '600', color: theme.colors.neutral['500'] }}>{translate('availableActions')}:</MyAppText>
            <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 16 }}>
                <CustomButton
                    text={translate('denied')}
                    style={{ backgroundColor: currentValue && currentValue.accepted === false ? '#A14C4C' : '#F3F3F3' }}
                    textStyle={{ color: currentValue && currentValue.accepted === false ? '#ffffff' : '#636363' }}
                    onPress={() => {
                        filesForm.setValue(fieldName, {
                            ...currentValue,
                            accepted: false
                        });
                        filesForm.trigger(fieldName);
                    }}
                    disabled={inViewMode}
                />
                <CustomButton
                    text={translate('approved_singular_m')}
                    style={{ backgroundColor: currentValue && currentValue.accepted === true ? '#609168' : '#F3F3F3' }}
                    textStyle={{ color: currentValue && currentValue.accepted === true ? '#ffffff' : '#636363' }}
                    onPress={() => {
                        filesForm.setValue(fieldName, {
                            ...currentValue,
                            accepted: true
                        });
                        filesForm.trigger(fieldName);
                    }}
                    disabled={inViewMode}
                />

                {!inViewMode && <CustomButton
                    text={translate('resendFile')}
                    style={{ backgroundColor: 'transparent', borderWidth: 1, borderColor: theme.colors.primary['500'], borderRadius: 4 }}
                    textStyle={{ color: theme.colors.primary['500'] }}
                    onPress={() => {
                        filesForm.setValue(fieldName, {
                            file: undefined,
                            fileName: '',
                            accepted: null
                        }, {
                            shouldValidate: true,
                            shouldDirty: true
                        });
                        filesForm.trigger(fieldName);
                    }}
                />}
            </View>
        </View>;
    }

    const tabs = [{
        key: 'inscriptionInfo',
        label: translate('inscriptionInfo'),
    }, {
        key: 'otherDocuments',
        label: translate('otherDocuments'),
    }];

    if (IsEditing) {
        tabs.push({
            key: 'companyCard',
            label: translate('companyCard'),
        });
    }

    if (needVideoAnalysis) {
        tabs.push({
            key: 'videoAnalysis',
            label: translate('videoAnalysis'),
        });
    }

    if (isEditingCloudProvider) {
        tabs.push({
            key: 'cloudProviderConnection',
            label: translate('cloudProviderConnection'),
        });

        tabs.push({
            key: 'imageProviderCompanies',
            label: translate('imageProviderCompanies'),
        });
    }

    function goBack() {
        navigation.canGoBack() ? navigation.goBack() : navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' });
    }

    return (
        <Container>
            <Header
                title={IsEditing ? translate('viewInscription') : translate('registerInscription')}
                goBackAction={goBack}
                actions={
                    <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 8 }}>
                        {IsEditing
                            ? <DropdownButton
                                viewStyle={{ height: '100%' }}
                                isLoading={isLoadingActions}
                                isButton
                                actions={getActions()}
                                buttonIcon={faChevronDown}
                                buttonType={'tertiary'}
                                buttonLabel='actions'
                                offsetX={getActionsOffsetX()}
                                offsetY={45}
                                size={164}
                            />
                            : null}
                        {inViewMode && !needVideoAnalysis && <CustomButton text={translate('goBack')} onPress={goBack} />}
                        {showSaveButton() ? <View style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 8 }}>
                            {IsEditing
                                ? <CustomButton
                                    text={translate('saveAndGoBack')}
                                    onPress={() => checkConnectionInfoChanged(true)}
                                    disabled={!isFormValid()}
                                    isLoading={isLoading}
                                    type='secondary'
                                />
                                : null}
                            <CustomButton
                                text={translate('save')}
                                onPress={checkConnectionInfoChanged}
                                disabled={!isFormValid()}
                                isLoading={isLoading}
                            />
                        </View> : null}
                    </View>
                }
            />
            {isFetchingInformation
                ? <View style={{ flex: 1, width: '100%', alignItems: 'center', justifyContent: 'center', }}>
                    <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                </View>
                :
                <Tabs
                    tabs={tabs}
                    endContent={
                        IsEditing
                            ? getInscriptionStatus({
                                status: form.watch('status'),
                                style: !windowInfo.isMobile && { alignSelf: 'flex-end' }
                            })
                            : null
                    }
                >
                    {({ selectedTab }) => {
                        if (selectedTab == 'inscriptionInfo') {
                            return <ScrollView contentContainerStyle={{ flexDirection: 'column', padding: 16, rowGap: 24, backgroundColor: theme.colors.white['500'] }}>
                                <SectionHeader title='registrationOfficerData' viewZIndex={3}>
                                    <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                                        <FormInput
                                            form={form}
                                            fieldName='name'
                                            required
                                            label={translate('fullName')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 3)}
                                            disabled={inViewMode}
                                        />
                                        <FormDropDownPicker
                                            searchable={false}
                                            label={translate('documentType')}
                                            items={documentTypeItems}
                                            zIndex={1}
                                            required
                                            fieldName={'documentType'}
                                            form={form}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={[styles.itemView(1), { minWidth: 132 }]}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='document'
                                            required
                                            label={form.watch('documentType').toUpperCase()}
                                            mode={form.watch('documentType') === 'cpf' ? 'NUMBER' : undefined}
                                            maxLength={form.watch('documentType') === 'cpf' ? 14 : undefined}
                                            customValidations={form.watch('documentType') === 'cpf' ? {
                                                validateCpf: (value) => validateCPF(value) || 'CPF inválido'
                                            } : {}}
                                            mask={form.watch('documentType') === 'cpf' ? (val) => {
                                                return val
                                                    .replace(/\D/g, '')
                                                    .replace(/(\d{3})(\d)/, '$1.$2')
                                                    .replace(/(\d{3})(\d)/, '$1.$2')
                                                    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
                                                    .replace(/(-\d{2})\d+?$/, '$1');
                                            } : undefined}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                            disabled={inViewMode}
                                        />
                                        <MyPhoneInput
                                            form={form}
                                            fieldName='phone'
                                            required
                                            placeholder='(00) 00000-0000'
                                            label={translate('contactPhoneNumber')}
                                            labelStyle={{
                                                ...styles.fieldsLabelStyle,
                                                height: 'auto'
                                            }}
                                            viewStyle={[styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2), { minWidth: 154 }]}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='role'
                                            required
                                            label={translate('roleInCompany')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 3)}
                                            disabled={inViewMode}
                                        />
                                    </View>
                                </SectionHeader>

                                <SectionHeader title='companyData' viewZIndex={2}>
                                    <View style={{ flexDirection: 'column', gap: 16 }}>
                                        <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                                            <FormInput
                                                form={form}
                                                fieldName='companyName'
                                                required
                                                label={translate('companyName')}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                                disabled={inViewMode}
                                            />
                                            <FormInput
                                                form={form}
                                                fieldName='tradeName'
                                                required
                                                label={translate('tradeName')}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                                disabled={inViewMode}
                                            />
                                            <MyCnpjInput
                                                form={form}
                                                fieldName='cnpj'
                                                required
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(1)}
                                                disabled={inViewMode}
                                            />
                                            <MyPhoneInput
                                                form={form}
                                                fieldName='commercialPhone'
                                                required
                                                placeholder='(00) 00000-0000'
                                                label={translate('contactPhoneNumber')}
                                                labelStyle={{
                                                    ...styles.fieldsLabelStyle,
                                                    height: 'auto'
                                                }}
                                                viewStyle={[styles.itemView(1), { minWidth: 154 }]}
                                                disabled={inViewMode}
                                            />
                                        </View>
                                        <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                                            <MyEmailInput
                                                form={form}
                                                fieldName='businessEmail'
                                                label={translate('corporateEmail')}
                                                placeholder={translate('corporateEmail')}
                                                required
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                                disabled={inViewMode}
                                            />

                                            <FormDropDownPicker
                                                label={translate('companyType')}
                                                items={companyTypeItems}
                                                multiple={true}
                                                mode='BADGE'
                                                zIndex={2}
                                                required
                                                fieldName={'companyType'}
                                                form={form}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                                disabled={inViewMode}
                                            />

                                            {form.watch('companyType').length > 0 && !form.watch('companyType').includes('cloud') && <CloudProviderDropdown
                                                form={form}
                                                fieldName={'companyCloudProviderId'}
                                                zIndex={1}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                                required
                                            />}

                                            <FormInput
                                                form={form}
                                                fieldName='companyWebsite'
                                                label={translate('companyWebsite')}
                                                labelStyle={styles.fieldsLabelStyle}
                                                viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                                disabled={inViewMode}
                                            />
                                        </View>
                                    </View>
                                </SectionHeader>

                                <SectionHeader title='addressData'>
                                    <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                                        <FormInput
                                            form={form}
                                            fieldName='cep'
                                            label={translate('cep')}
                                            required
                                            maxLength={9}
                                            mode='NUMBER'
                                            mask={(val) => {
                                                return val
                                                    .replace(/\D/g, '')
                                                    .replace(/(\d{5})(\d)/, '$1-$2')
                                                    .replace(/(-\d{3})\d+?$/, '$1');
                                            }}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 3)}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='address'
                                            label={translate('street')}
                                            required
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 5)}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='number'
                                            label={translate('number')}
                                            required
                                            mode='NUMBER'
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 2)}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='city'
                                            required
                                            label={translate('city')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 4)}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='neighborhood'
                                            required
                                            label={translate('neighborhood')}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(windowInfo.isMobileOrTablet ? 1 : 4)}
                                            disabled={inViewMode}
                                        />
                                        <FormInput
                                            form={form}
                                            fieldName='state'
                                            label={translate('uf')}
                                            customValidations={{
                                                validUf: (val: string) => validUfs.includes(val.toLowerCase()) || translate('invalid_uf')
                                            }}
                                            required
                                            maxLength={2}
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={styles.itemView(1)}
                                            disabled={inViewMode}
                                        />
                                    </View>
                                </SectionHeader>

                                {(form.watch('integratedImageCamerasQty') > 0 || form.watch('integratedCloudCamerasQty') > 0) && <SectionHeader title='integratedCamerasQuantity'>
                                    <View style={styles.sectionDisplay(windowInfo.isMobileOrTablet)}>
                                        {form.watch('integratedImageCamerasQty') > 0 && <FormInput
                                            form={form}
                                            fieldName='integratedImageCamerasQty'
                                            label={translate('camerasOwnedNumber')}
                                            mode='NUMBER'
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={[{ gap: 8 }, windowInfo.isMobileOrTablet ? {} : { maxWidth: '15%' }]}
                                            disabled={true}
                                        />}
                                        {form.watch('integratedCloudCamerasQty') > 0 && <FormInput
                                            form={form}
                                            fieldName='integratedCloudCamerasQty'
                                            label={translate('camerasViaCloudNumber')}
                                            mode='NUMBER'
                                            labelStyle={styles.fieldsLabelStyle}
                                            viewStyle={[{ gap: 8 }, windowInfo.isMobileOrTablet ? {} : { maxWidth: '15%' }]}
                                            disabled={true}
                                        />}
                                    </View>
                                </SectionHeader>}

                                <SectionHeader title='visualIdentity'>
                                    <FormFileUploader
                                        form={filesForm}
                                        fieldName='visualIdentity'
                                        labelStyle={styles.fieldsLabelStyle}
                                        label={translate('companyLogo')}
                                        supportedFormatsText={translate('supported', { fileTypes: ['.jpg', '.jpeg', '.png'].join(', ') })}
                                        recommendedSizeText={translate('recommendedSize', { size: '240x120' })}
                                        required
                                        maxFileSize={10}
                                        allowedFileTypes={['image/jpg', 'image/png', 'image/jpeg']}
                                        actions={<>
                                            {getFileActions('visualIdentity')}
                                        </>}
                                        defaultValues={{
                                            file: undefined,
                                            fileName: '',
                                            accepted: true
                                        }}
                                        emptyObjectValues={{
                                            file: undefined,
                                            fileName: '',
                                            accepted: null
                                        }}
                                        previewStyle={{ flexDirection: windowInfo.isMobile ? 'column' : 'row' }}
                                        disabled={inViewMode}
                                        customValidations={{
                                            hasFileApproval: (val) => val && val.accepted != null || translate('file_validation_required')
                                        }}
                                    />
                                </SectionHeader>

                                <SectionHeader title='requiredAttachments'>
                                    <View style={{ flexDirection: 'column', gap: 16 }}>
                                        {[
                                            'legalEntityRegistrationProofAndStatus',
                                            'companyAdministratorDocument',
                                            'socialContract',
                                            'assignmentAgreement'
                                        ].map(item => (
                                            <FormFileUploader
                                                key={item}
                                                form={filesForm}
                                                fieldName={item}
                                                labelStyle={styles.fieldsLabelStyle}
                                                label={translate(item)}
                                                supportedFormatsText={translate('supported', { fileTypes: ['.jpg', '.jpeg', '.png', '.pdf'].join(', ') })}
                                                required
                                                maxFileSize={10}
                                                allowedFileTypes={['image/jpg', 'image/png', 'image/jpeg', 'application/pdf']}
                                                actions={getFileActions(item as keyof ApprovedCompaniesFiles)}
                                                defaultValues={{
                                                    file: undefined,
                                                    fileName: '',
                                                    accepted: true
                                                }}
                                                emptyObjectValues={{
                                                    file: undefined,
                                                    fileName: '',
                                                    accepted: null
                                                }}
                                                previewStyle={{ flexDirection: windowInfo.isMobile ? 'column' : 'row' }}
                                                customValidations={{
                                                    hasFileApproval: (val) => val && val.accepted != null || translate('file_validation_required')
                                                }}
                                                allowDownload={IsEditing && form.watch('status') != 'failed'}
                                                disabled={inViewMode}
                                            />
                                        ))}
                                    </View>
                                </SectionHeader>

                                <SectionHeader title='publicCallAcceptance'>
                                    <MyCheckbox
                                        label={<MyAppText style={styles.publicCallText}>
                                            {parseTextWithBold(translate('publicCallAcceptanceConcentMessage'))}
                                        </MyAppText>}
                                        form={form}
                                        fieldName='publicCallAcceptance'
                                        style={{ flex: 1 }}
                                        customIcon={<FontAwesomeIcon icon={faCheck} fontSize={10} color={theme.colors.white['500']} />}
                                        checkBoxStyle={{
                                            backgroundColor: form.watch('publicCallAcceptance')
                                                ? theme.colors.primary['500']
                                                : 'transparent',
                                            borderWidth: 1,
                                            borderColor: theme.colors.neutral['300']
                                        }}
                                        disabled={inViewMode}
                                    />
                                </SectionHeader>
                            </ScrollView>;
                        } else if (selectedTab == 'otherDocuments') {
                            return <ScrollView contentContainerStyle={{ flexDirection: 'column', padding: 16, rowGap: 24, backgroundColor: theme.colors.white['500'] }}>
                                <FormFileUploader
                                    form={additionalDocuments}
                                    fieldName='files'
                                    multiple
                                    allowDownload={IsEditing && form.watch('status') != 'failed'}
                                    uploadComponentStyle={additionalDocuments.watch('files')?.length > 0 ? {
                                        position: 'sticky',
                                        zIndex: 10,
                                        width: '98%',
                                        top: 0,
                                        backgroundColor: theme.colors.white['500']
                                    } : {}}
                                    supportedFormatsText={translate('supported', { fileTypes: ['.jpg', '.jpeg', '.png', '.pdf'].join(', ') })}
                                    allowedFileTypes={['image/jpg', 'image/png', 'image/jpeg', 'application/pdf']}
                                    disabled={inViewMode}
                                />
                            </ScrollView>;
                        } else if (selectedTab == 'companyCard') {
                            return inscription?.status === 'published'
                                ? inscription && <InscriptionCard inscription={inscription} />
                                : <View>
                                    <MyAppText style={{ fontSize: 14, fontWeight: '400', color: theme.colors.neutral['500'] }}>{translate('necessaryCompanyToBePublished')}</MyAppText>
                                </View>;
                        } else if (selectedTab == 'videoAnalysis') {
                            return <VideoAnalysisForm form={videoAnalysisForm} />;
                        } else if (selectedTab == 'cloudProviderConnection') {
                            return <CloudProviderConnectionForm form={cloudProviderConnectionForm} inViewMode={inViewMode} />;
                        } else if (selectedTab === 'imageProviderCompanies') {
                            const imageProviderCompanies = inscription?.imageProviderCompanies ?? [];

                            const allImageProviderCompanies = inscription?.companyType === 'both' && inscription.integrationId
                                ? [{ companyName: inscription.companyName, integrationId: inscription.integrationId, isSelf: true }, ...imageProviderCompanies]
                                : imageProviderCompanies;

                            return <ImageProviderCompanies imageProviderCompanies={allImageProviderCompanies} />;
                        }
                    }}
                </Tabs>
            }

            <InscriptionChangeStatusModal
                currentStatus={form.watch('status')}
                isModalVisible={isChangeStatusModalVisible}
                setModalVisible={setIsChangeStatusModalVisible}
                onConfirm={async (newValues) => {
                    await updateStatus(newValues);
                }}
            />
            <InscriptionPublishConfirmModal
                isModalVisible={isPublishUnpublishModalVisible}
                setModalVisible={setIsPublishUnpublishModalVisible}
                mode={publishMode}
                onConfirm={async (newStatus) => {
                    await updateStatus({ status: newStatus });
                }}
            />
            {inscription && <DeleteInscriptionModal
                onConfirm={() => navigation.navigate('ThirdPartyApprovedCompanies', { screen: 'InscriptionsManagement' })}
                selectedInscription={inscription}
                isModalVisible={isDeleteModalVisible}
                setModalVisible={setIsDeleteModalVisible}
            />}
            <WarningModal
                isModalVisible={showWarningModal}
                setIsModalVisible={setShowWarningModal}
                text={translate('cloudProviderConnectionInfoChangeWarning')}
                onSubmit={() => {
                    setShowWarningModal(false);
                    formSubmit();
                }}
                onClose={() => {
                    setShowWarningModal(false);
                    if (inscription) {
                        cloudProviderConnectionForm.reset({
                            cloudProviderUsername: inscription.cloudProviderUsername,
                            cloudProviderPassword: inscription.cloudProviderPassword,
                            cloudProviderEndpoint: inscription.cloudProviderEndpoint
                        });
                    }
                }}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    sectionHeaderTitle: {
        color: theme.colors.primary['500'],
        fontWeight: '600',
        fontSize: 16,
    },
    fieldsLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    },
    publicCallText: {
        color: theme.colors.neutral['400'],
        fontWeight: '500',
        fontSize: 18,
    },
    sectionDisplay: (isSmallView: boolean) => ({
        gap: 16,
        flexDirection: isSmallView ? 'column' : 'row',
    }),
    itemView: (flex: number) => ({
        gap: 8,
        flex
    })
}));
